import { QueryKey } from '@tanstack/react-query'
import { enums } from '@weenat/client'
import { getDeviceCommonName } from '@weenat/client/dist/core/devices'
import { isModelVirtualWeatherStation } from '@weenat/client/dist/core/devices/isVirtualWeatherStation'
import { HorizonOpt } from '@weenat/client/dist/core/horizons'
import { getCountByMetric, useMetricProvider } from '@weenat/client/dist/core/metrics'
import { useMyFarmItemActions } from '@weenat/client/dist/core/my-farm'
import { useToggleIsFavorite } from '@weenat/client/dist/core/plots'
import { getMetricLastUpdateFromSummary } from '@weenat/client/dist/core/summary'
import {
  BackgroundMapUniverseMetric,
  Universe,
  UniverseMetric,
  allUniverseMetrics,
  getAvailableUniversesFromMetrics
} from '@weenat/client/dist/core/universes'
import { CommonKey, MeasurementKey } from '@weenat/client/dist/enums/MetricIds'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources'
import {
  PlotSummary,
  StationSummary,
  isPlotSummary,
  isStationSummary
} from '@weenat/client/dist/resources/measurements.type'
import { Plot } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import useDisclosure from 'app/src/hooks/useDisclosure'
import { RestrictionReason, useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import useMapIntent from 'app/src/hooks/useMapIntent'
import useToasts from 'app/src/hooks/useToasts'
import ActionItem from 'app/src/kit/ActionItem'
import DelimitedBox from 'app/src/kit/DelimitedBox'
import { IconProps } from 'app/src/kit/Icon'
import Icons from 'app/src/kit/Icons'
import InteractiveCard from 'app/src/kit/InteractiveCard'
import StackedMetrics from 'app/src/kit/StackedMetrics'
import StackedUniverses from 'app/src/kit/StackedUniverses'
import Text from 'app/src/kit/Text'
import UpdateInformation from 'app/src/kit/UpdateInformation'
import {
  Dropdown,
  DropdownContent,
  DropdownTrigger
} from 'app/src/kit/fields/Select/SelectDropdown'
import TextTooltip from 'app/src/kit/tooltips/TextTooltip'
import { useToggleFeature, useUniverse } from 'app/state'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { MouseEvent, ReactNode, memo, useCallback, useMemo } from 'react'
import { styled } from 'styled-components'
import PlotListItemMetricSummary from './plotListItem/PlotListItemMetricSummary'

export const PLOT_OR_STATION_CARD_BORDER_WIDTH = 1
export const PLOT_OR_STATION_CARD_HEIGHT = 64

const Titles = styled(Flex)`
  flex: 1;
  align-items: center;
  gap: 4px;
  overflow: hidden;
`

const ActionsContainer = styled(Flex)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  padding: ${(p) => p.theme.spacings.md}px;
  gap: ${(p) => p.theme.spacings.md}px;
`

const StyledCard = styled(InteractiveCard)`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  height: ${PLOT_OR_STATION_CARD_HEIGHT}px;
  border-radius: 0px;
  border-width: 0 0 ${PLOT_OR_STATION_CARD_BORDER_WIDTH}px 0;
  border-color: ${(p) => p.theme.colors.grayscale[100]};

  padding: 0;
  overflow: hidden;
`

interface PureFarmItemInfoProps {
  name: string
  reverseGeocoding: string | null
  Icon: ReactNode
  kind: 'plot' | 'station'
  deviceCount: number
  hasVirtualDevice: boolean
}

const PureFarmItemInfo: FC<PureFarmItemInfoProps> = ({
  name,
  reverseGeocoding,
  kind,
  Icon,
  deviceCount,
  hasVirtualDevice
}) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const [{ smv }] = useToggleFeature()

  return (
    <Flex $alignItems='center' $p='md' $gap='lg' $flex={1}>
      {Icon}
      <Titles>
        <Flex $flexDirection='column' $alignItems='start' $gap='sm'>
          <Box $minWidth={0} $maxWidth='28vw'>
            <Text $fontWeight='bold' $ellipsis>
              {name}
            </Text>
          </Box>
          <Flex $flexDirection='row' $gap='sm'>
            <Text $fontSize='sm' $color={'grayscale.700'} $ellipsis style={{ maxWidth: '17vw' }}>
              {!isNil(reverseGeocoding) && !isEmpty(reverseGeocoding)
                ? reverseGeocoding
                : t('models.device.unknown_location', { capitalize: true })}
            </Text>
            {kind === 'plot' ? (
              <>
                {deviceCount !== 0 ? (
                  <>
                    {'·'}
                    <Text $fontSize='sm' $fontWeight='semiBold' $color={'secondary.500'} $ellipsis>
                      {deviceCount == 1
                        ? t('models.device.model.singular_item_count', { count: deviceCount })
                        : t('models.device.model.item_count', { count: deviceCount })}
                    </Text>
                  </>
                ) : (
                  <></>
                )}
                {smv ? null : hasVirtualDevice ? (
                  <>
                    {'·'}
                    <Text $fontSize='sm' $fontWeight='semiBold' $color={'primary.500'} $ellipsis>
                      {t(
                        org.billingStatus === enums.BillingStatus.expert
                          ? 'dataSources.meteo_vision'
                          : 'dataSources.spatialized_weather'
                      )}
                    </Text>
                  </>
                ) : null}
              </>
            ) : null}
          </Flex>
        </Flex>
      </Titles>
    </Flex>
  )
}

const FarmItemInfo = memo(PureFarmItemInfo)

interface PureFarmItemActionsProps {
  item: Pick<Plot | Station, 'id' | 'location'>
  kind: 'plot' | 'station'
  isFavorite: boolean
  isFavoriteRequestLoading: boolean
  onFavoritePress: IconProps['onPress']
}

const PureFarmItemActions: FC<PureFarmItemActionsProps> = ({
  item,
  kind,
  isFavoriteRequestLoading,
  isFavorite,
  onFavoritePress
}) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const [{ on_nav_favorites }] = useToggleFeature()

  const { isOpen, toggle, close } = useDisclosure()
  const mapAppUrl = useMapIntent({
    lat: item.location?.coordinates[1],
    lng: item.location?.coordinates[0]
  })

  const groups = useMyFarmItemActions({
    itemId: item.id,
    itemKind: kind,
    currentOrgId: org.id,
    onGeolocationActionPress: () => {
      window.open(mapAppUrl, '_blank')
    },
    newTabHandler: () => {
      window.open(
        `${
          // eslint-disable-next-line no-restricted-properties
          window.location.href.split('?')[0] +
          '/' +
          (kind === 'plot' ? 'plots' : 'stations') +
          '/' +
          item.id
        }`,
        '_blank'
      )
    }
  })

  const handlePress = (e: MouseEvent<Element>) => {
    e.preventDefault()
    toggle()
  }

  return (
    <ActionsContainer>
      {!on_nav_favorites ? (
        <TextTooltip
          restMs={1000}
          content={t(`actions.${isFavorite ? 'remove_from_favorite' : 'add_to_favorite'}`)}
        >
          <Icons.Star
            $size='lg'
            $isLoading={isFavoriteRequestLoading}
            onPress={onFavoritePress}
            $color={isFavorite ? 'primary.500' : 'grayscale.300'}
          />
        </TextTooltip>
      ) : null}
      <Dropdown isVisible={isOpen} onVisibleChange={toggle} marginFromParent={8}>
        <DropdownTrigger>
          <Icons.MoreHoriz
            onPress={handlePress}
            $size='lg'
            $backgroundColor={isOpen ? 'primary.200' : undefined}
          />
        </DropdownTrigger>
        <DropdownContent width={392}>
          {groups.map(({ id, actions }) =>
            !isEmpty(actions) ? (
              <DelimitedBox key={id} $py={'sm'}>
                {actions.map((action) => {
                  return <ActionItem key={action.id} action={action} onActionFinished={close} />
                })}
              </DelimitedBox>
            ) : null
          )}
        </DropdownContent>
      </Dropdown>
    </ActionsContainer>
  )
}

const FarmItemActions = memo(PureFarmItemActions)

interface FarmItemMetricsProps {
  availableMetrics: DeviceAvailableMeasure[]
  data: PlotSummary['data']
  focusedHorizon: number | null
  isDiscrete: boolean
  isRestricted: boolean
  /**
   * Whether the device is virtual or not
   * PRE smv this is coming from useMetricProvider
   * POST smv this is coming directly from the device model
   * @default false
   */
  isVirtualDevice: boolean
  lastUpdateThresholds: { warningThreshold: number; dangerThreshold?: number }
  metric: BackgroundMapUniverseMetric | undefined
  needsConfiguration?: (MeasurementKey | CommonKey)[] | null | boolean
  providerName: string | undefined
  reason: RestrictionReason
  summary: PlotSummary | StationSummary
  timestamp: number | undefined
  /** @deprecated use lastUpdateThresholds - remove with marker_redesign feature flag */
  warningThreshold?: number
  timezone?: string
}

const PureFarmItemMetrics: FC<FarmItemMetricsProps> = ({
  availableMetrics,
  data,
  focusedHorizon,
  isDiscrete,
  isRestricted,
  isVirtualDevice,
  lastUpdateThresholds,
  metric,
  needsConfiguration,
  providerName,
  reason,
  summary,
  timestamp,
  warningThreshold,
  timezone
}) => {
  const { t } = useIntl()
  const [currentUniv] = useUniverse()
  const [{ marker_redesign }] = useToggleFeature()

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-expect-error
  const availableUniverses = getAvailableUniversesFromMetrics(availableMetrics)

  const sortedMetrics = useMemo(
    () =>
      availableMetrics.sort((a, b) => {
        return allUniverseMetrics.indexOf(a) > allUniverseMetrics.indexOf(b) ? 1 : -1
      }),
    [availableMetrics]
  )

  return (
    <Flex
      $flexDirection='column'
      $alignItems='flex-end'
      $gap='md'
      $py='md'
      $justifyContent='center'
    >
      {currentUniv === Universe.overview && (
        <Flex $flexDirection='row' $gap='lg'>
          {isPlotSummary(summary) && !isEmpty(availableUniverses) && (
            <StackedUniverses universes={availableUniverses} />
          )}
          {!isEmpty(availableMetrics) && <StackedMetrics metrics={sortedMetrics} />}
        </Flex>
      )}
      {currentUniv !== Universe.overview &&
        !isDiscrete &&
        (isNil(needsConfiguration) ||
          isEmpty(needsConfiguration) ||
          needsConfiguration === false) && (
          <>
            <PlotListItemMetricSummary
              metric={metric}
              data={data}
              focusedHorizon={focusedHorizon}
              isVirtualDevice={isVirtualDevice}
              isRestricted={isRestricted}
              reason={reason}
              provider={providerName}
              variant={'list'}
              timezone={timezone}
            />
            <Flex>
              <UpdateInformation
                timestamp={timestamp}
                isVirtualMetric={marker_redesign ? undefined : isVirtualDevice}
                warningThreshold={
                  marker_redesign ? lastUpdateThresholds.warningThreshold : warningThreshold
                }
                dangerThreshold={lastUpdateThresholds.dangerThreshold}
                name={providerName}
                timezone={timezone}
              />
            </Flex>
          </>
        )}
      {currentUniv !== Universe.overview &&
        !isNil(needsConfiguration) &&
        !isNil(metric) &&
        Array.isArray(needsConfiguration) &&
        needsConfiguration.includes(metric) && (
          <Flex
            $backgroundColor='grayscale.100'
            $py='md'
            $px='lg'
            $gap='md'
            $borderRadius='sm'
            $alignItems='center'
          >
            <Icons.Wrench $size='sm' />
            <Text $fontWeight='medium' $fontSize='sm'>
              {t('models.plot.misc.required_informations')}
            </Text>
          </Flex>
        )}
    </Flex>
  )
}

const FarmItemMetrics = memo(PureFarmItemMetrics)

interface PlotOrStationCardProps {
  availableHorizonOptions: HorizonOpt[]
  focusedHorizon: number | null
  focusedMetric: UniverseMetric
  hasNoDataForHorizon: boolean
  onHorizonChange: (newHorizon: number | null) => void
  summariesQueryCacheKey: QueryKey
  summary: PlotSummary | StationSummary
  isExpertOrg: boolean
  onPress?: () => void
  isTooltip?: boolean
  onClose?: () => void
}

const PlotOrStationCard: FC<PlotOrStationCardProps> = ({
  focusedHorizon = null,
  focusedMetric,
  summariesQueryCacheKey,
  summary,
  isExpertOrg
}) => {
  const { t } = useIntl()
  const { addSuccessToast } = useToasts()
  const [{ smv }] = useToggleFeature()

  const item = isPlotSummary(summary) ? summary.metadata.plot : summary.metadata.station

  const { countByMetric } = getCountByMetric(summary)
  const isDiscrete = isNil(focusedMetric) || !countByMetric.has(focusedMetric)

  const config = isPlotSummary(summary)
    ? summary.metadata.plot.currentMeasurementConfigs.find(
        (measure) => measure.metric === (focusedMetric ?? 'RR')
      )
    : undefined

  const availableMeasuresArray = isPlotSummary(summary)
    ? [
        ...new Set(
          summary.metadata.plot.currentMeasurementConfigs
            .filter(
              (measure) =>
                isDiscrete ||
                (!isNil(config) && 'deviceId' in config && measure.deviceId === config?.deviceId)
            )
            .map((measure) => measure.metric)
        )
      ]
    : summary.metadata.station.availableMeasures

  const needsConfiguration = isPlotSummary(summary) && summary.metadata.notConfigured
  const hasVirtualDevice = isPlotSummary(summary)
    ? !isNil(summary.metadata.plot.hasVirtualDevice) && summary.metadata.plot.hasVirtualDevice
    : false

  const {
    isVirtualDevice,
    warningThreshold,
    lastUpdateThresholds,
    name: _technicalName,
    deviceIds
  } = useMetricProvider({
    resource: item,
    metric: focusedMetric ?? 'RR',
    isExpertOrg,
    smv
  })

  const isVirtualWaterTank =
    focusedMetric === 'WATER_TANK_LEVEL' &&
    isPlotSummary(summary) &&
    summary.metadata.plot.currentMeasurementConfigs
      .map((measurement) => measurement.metric)
      .includes('VIRTUAL_WATER_TANK_LEVEL')

  const technicalName = isVirtualWaterTank ? t('dataSources.soil_vision') : _technicalName

  const commonName =
    !isNil(config) && 'brand' in config
      ? getDeviceCommonName({
          availableMeasures: availableMeasuresArray,
          brand: config.brand!,
          endUserName: [config.model, '-']
        })
      : isStationSummary(summary)
        ? getDeviceCommonName({
            availableMeasures: summary.metadata.station.availableMeasures,
            brand: summary.metadata.station.brand,
            endUserName: summary.metadata.station.endUserName
          })
        : technicalName

  const providerName =
    isVirtualDevice || commonName === technicalName
      ? technicalName
      : `${commonName} (${technicalName})`

  const { name, isFavorite } = item

  const { mutation: toggleIsFavorite, request: isFavoriteRequest } = useToggleIsFavorite({
    summary,
    summariesQueryCacheKey,
    onSuccess: (data) => {
      addSuccessToast(
        t(
          data.isFavorite
            ? 'actions.add_plot_to_favorites_success'
            : 'actions.remove_plot_from_favorites_success',
          { plotName: name }
        )
      )
    }
  })

  const onFavoritePress = useCallback(
    async (e: MouseEvent<Element>) => {
      // Otherwise we open the summary
      e.preventDefault()
      await toggleIsFavorite()
    },
    [toggleIsFavorite]
  )

  const lastUpdate = getMetricLastUpdateFromSummary({
    summary,
    metric: focusedMetric,
    horizonId: deviceIds.length > 1 ? null : focusedHorizon
  })

  const { isRestricted, reason } = useIsAppAccessRestricted()

  const isVirtualWeatherStation = smv
    ? isStationSummary(summary)
      ? isModelVirtualWeatherStation(summary.metadata.station.model)
      : false
    : isVirtualDevice

  const deviceCount = isPlotSummary(summary)
    ? new Set(
        summary.metadata.plot.currentMeasurementConfigs
          .map((measure) => measure.deviceId)
          .filter((measure) => !isNil(measure))
      ).size
    : 1

  return (
    <StyledCard>
      <FarmItemInfo
        name={name}
        reverseGeocoding={item.reverseGeocoding}
        kind={isPlotSummary(summary) ? 'plot' : 'station'}
        Icon={
          isPlotSummary(summary) ? (
            <Icons.PlotsFilled $size='lg' />
          ) : isModelVirtualWeatherStation(summary.metadata.station.model) ? (
            <Icons.SingleVirtualSensorFilled $size='lg' />
          ) : (
            <Icons.SingleSensorFilled $size='lg' />
          )
        }
        deviceCount={deviceCount}
        hasVirtualDevice={hasVirtualDevice}
      />
      <Flex $gap='md' $px='md' $borderLeft='1px solid' $borderLeftColor='grayscale.100'>
        <FarmItemMetrics
          metric={focusedMetric}
          summary={summary}
          isVirtualDevice={isVirtualWeatherStation}
          isDiscrete={isDiscrete}
          availableMetrics={availableMeasuresArray}
          needsConfiguration={needsConfiguration}
          warningThreshold={warningThreshold}
          lastUpdateThresholds={lastUpdateThresholds}
          timestamp={lastUpdate}
          providerName={providerName}
          data={summary.data}
          isRestricted={isRestricted}
          reason={reason}
          focusedHorizon={focusedHorizon}
        />

        <FarmItemActions
          item={item}
          kind={isPlotSummary(summary) ? 'plot' : 'station'}
          isFavorite={isFavorite}
          isFavoriteRequestLoading={isFavoriteRequest.isPending}
          onFavoritePress={onFavoritePress}
        />
      </Flex>
    </StyledCard>
  )
}

export default memo(PlotOrStationCard)
